import { Component, OnInit, ViewChild, AfterViewInit, OnDestroy, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { merge, Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { trigger, style, animate, transition } from '@angular/animations';

import { PaymentsDataSource } from '../../_cdk/payments-data-source';
import { PaymentService } from './../../_services/payment.service';
import { LoaderService } from './../../_services/loader.service';
import { IntegrationService } from './../../_services/integration.service';
import { ConfigurationService } from './../../_services/configuration.service';
import { IconRegistryService } from './../../_services/icon-registry.service';
import { DrawerService } from '../../_services/drawer.service';

import { IntegrationDialogComponent } from './../integration-dialog/integration-dialog.component';
import { IdentifiersDialogComponent } from './../identifiers-dialog/identifiers-dialog.component';
import { environment } from './../../../environments/environment.prod';
import { Integration } from '../../_models/integration';
import { IQParameter } from '../../_models/iqparameter';

export interface License {
  server: string;
  wizard: string;
  html: string;
  connect: string;
  js: string;
  ios: string;
  android: string;
}

@Component({
  selector: 'app-transactions-list',
  templateUrl: './transactions-list.component.html',
  styleUrls: ['./transactions-list.component.scss'],
  standalone: false,
  animations: [
    trigger('inOutButtons', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(100%)' }),
        animate('600ms ease-in', style({ opacity: 1, transform: 'translateX(0%)' })),
      ]),
    ]),
  ],
})
export class TransactionsListComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly paymentService = inject(PaymentService);
  private readonly loader = inject(LoaderService);
  private readonly integrationService = inject(IntegrationService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly configurationService = inject(ConfigurationService);
  private readonly iconRegistryService = inject(IconRegistryService);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly drawerService = inject(DrawerService);
  public breakpointObserver = inject(BreakpointObserver);

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort!: MatSort;

  displayedColumns: string[] = [];
  paymentsSource!: PaymentsDataSource;
  pageSize = 5;
  pageSizeOptions: number[] = [5, 10, 25, 50, 100];

  gateways: string[] = ['IGFS', 'Computop', 'MyBank'];
  circuiti: string[] = ['MasterCard', 'Visa', 'Diners', 'American Express'];
  filters: string[] = ['amount', 'shop_id', '_id'];
  qry: IQParameter[] = [];
  licenses: License = { server: null, wizard: null, html: null, connect: null, js: null, ios: null, android: null };

  host = environment.host;
  redirect_host = environment.redirect_host;
  copiedKey = $localize`:@@snackBarMessage-CopiedKey:Chiave copiata`;

  form?: UntypedFormGroup;
  filterBy: string = '';
  mapFilter: Record<string, string> = {
    amount: $localize`:@@placeholder-Amount:Importo`,
    shop_id: 'Shop ID',
    _id: $localize`:@@idpayment:ID pagamento`,
  };
  activeMediaQuery: string = '';
  drawerSubscription!: Subscription;

  constructor() {
    this.iconRegistryService.registryIconTransaction();
    this.setHost();
  }

  ngOnInit(): void {
    this.paymentsSource = new PaymentsDataSource(this.paymentService, this.loader);
    this.getIntegrations();
    this.form = this.fb.group({
      cerca: [''],
      data: [''],
    });
    this.genLabels();
    this.resizeTable();
    this.subscribeToDrawer();
  }

  ngAfterViewInit(): void {
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    merge(this.sort.sortChange, this.paginator.page)
      .pipe(startWith({}), map(() => this.loadPayments()))
      .subscribe();
  }

  ngOnDestroy(): void {
    this.drawerSubscription?.unsubscribe();
  }

  loadPayments() {
    if (!this.sort) return;
    const sort = this.sort.direction === 'desc' ? '-' + this.sort.active : this.sort.active;
    this.paymentsSource.loadPayments(this.qry, sort, this.paginator.pageIndex, this.paginator.pageSize);
  }

  applicaFiltri() {
    if (!this.form) return;
    this.qry = [];
    this.paginator.pageIndex = 0;
    if (this.form.value.data) {
      const date = this.form.value.data.format('YYYY-MM-DD');
      this.qry.push({ key: 'from_date', value: date });
    }
    if (this.form.value.cerca) {
      const value = this.filterBy === 'amount' ? this.form.value.cerca.toFixed(2) : this.form.value.cerca;
      this.qry.push({ key: this.filterBy, value });
    }
    this.loadPayments();
  }

  selectChange(event: any) {
    this.form.controls.cerca.setValue(null);
  }

  showIntegrationDialog(id: string) {
    this.paymentService.getPaymentID(this.licenses.server, id).subscribe((encryptedID) => {
      if (!encryptedID) return;
      const tabsData = { licenses: this.licenses, paymentID: encryptedID };
      this.dialog.open(IntegrationDialogComponent, {
        panelClass: 'integration-dialog-container',
        width: '58%',
        data: tabsData,
      });
    });
  }

  setHost() {
    this.configurationService.getPublicSettings().subscribe((data: any) => {
      const settings = data?.settings || {};
      this.host = settings.host || this.host;
      this.redirect_host = settings.host_redirect || this.redirect_host;
    });
  }

  getIntegrations() {
    this.integrationService.getIntegrationsByMerchant().subscribe((data: any) => {
      data?.items?.forEach((element: Integration) => {
        this.licenses[element.type.toLowerCase()] = element.license_key;
      });
    });
  }

  resizeTable() {
    const breakpoints = [Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium, Breakpoints.Large, Breakpoints.XLarge];
    this.breakpointObserver.observe(breakpoints).subscribe((bpState: BreakpointState) => {
      if (bpState.breakpoints[Breakpoints.XSmall]) {
        this.activeMediaQuery = Breakpoints.XSmall;
        this.displayedColumns = ['state', 'amount', 'actions'];
      } else if (bpState.breakpoints[Breakpoints.Small]) {
        this.activeMediaQuery = Breakpoints.Small;
        this.displayedColumns = ['createdAt', 'state', 'amount', 'identificativi', 'actions'];
      } else if (bpState.breakpoints[Breakpoints.Medium]) {
        this.activeMediaQuery = Breakpoints.Medium;
        this.displayedColumns = ['createdAt', 'state', 'state_3ds', 'amount', 'identificativi', 'actions'];
      } else if (bpState.breakpoints[Breakpoints.Large]) {
        this.activeMediaQuery = Breakpoints.Large;
        this.displayedColumns = ['createdAt', 'state', 'state_3ds', 'amount', 'circuit', 'wallet_token', 'card_token', 'number', 'transactionID', 'identificativi', 'actions'];
        if (this.drawerService.isOpen) {
          this.displayedColumns = ['createdAt', 'state', 'state_3ds', 'amount', 'circuit', 'wallet_token', 'card_token', 'identificativi', 'actions'];
        }
      } else if (bpState.breakpoints[Breakpoints.XLarge]) {
        this.activeMediaQuery = Breakpoints.XLarge;
        this.displayedColumns = ['createdAt', 'state', 'state_3ds', 'amount', 'circuit', 'wallet_token', 'card_token', 'number', 'transactionID', 'identificativi', 'actions'];
      }
    });
  }

  subscribeToDrawer() {
    this.drawerSubscription = this.drawerService.isOpen$.subscribe((isOpen) => {
      if (this.activeMediaQuery !== Breakpoints.Large) return;
      this.displayedColumns = isOpen
        ? ['createdAt', 'state', 'state_3ds', 'amount', 'circuit', 'wallet_token', 'card_token', 'number', 'identificativi', 'actions']
        : ['createdAt', 'state', 'state_3ds', 'amount', 'circuit', 'wallet_token', 'card_token', 'number', 'transactionID', 'identificativi', 'actions'];
    });
  }

  copyToken(token: string) {
    navigator.clipboard.writeText(token);
    this.openSnackBar(this.copiedKey, '');
  }

  openSnackBar(message: string, action: string) {
    this.snackBar.open(message, action, { duration: 2000 });
  }

  redirect(id: string) {
    if (!this.licenses.wizard) {
      this.openSnackBar($localize`:@@error-LicenseNotAvailable:Licenza non disponibile`, 'Chiudi');
      return;
    }

    this.paymentService.getPaymentID(this.licenses.wizard, id).subscribe({
      next: (res) => {
        if (!res) {
          this.openSnackBar($localize`:@@error-PaymentNotFound:Pagamento non trovato`, 'Chiudi');
          return;
        }
        const host = this.redirect_host || environment.redirect_host;
        const fullUrl = `${host}${res}/${this.licenses.wizard}`;
        this.openWindow(fullUrl);
      },
      error: () => {
        this.openSnackBar($localize`:@@error-RequestFailed:Richiesta fallita`, 'Chiudi');
      }
    });
  }

  private openWindow(url: string) {
    try {
      const newWindow = window.open(url, '_blank');
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        window.location.href = url;
      }
    } catch {
      window.location.href = url;
    }
  }

  genLabels() {
    if (!this.paginator) return;
    this.paginator._intl.itemsPerPageLabel = $localize`:@@table-itemsPerPageLabel:Elementi per pagina`;
    this.paginator._intl.nextPageLabel = $localize`:@@table-nextPageLabel:Pagina successiva`;
    this.paginator._intl.previousPageLabel = $localize`:@@table-previousPageLabel:Pagina precedente`;
    this.paginator._intl.firstPageLabel = $localize`:@@table-firstPageLabel: Prima pagina`;
    this.paginator._intl.lastPageLabel = $localize`:@@table-lastPageLabel:Ultima pagina`;
  }

  openDialogIdentifiers(operation: any) {
    this.dialog.open(IdentifiersDialogComponent, {
      panelClass: 'identifiers-dialog-container',
      width: '58%',
      data: operation,
    });
  }
}
